import React, { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { SxProps, Theme } from "@mui/material/styles";
import { EmtChip } from "./EmtChip";
import { EmtIcon } from "./EmtIcon";

interface SelectOptionType {
  value: string | number;
  label: string;
  caption?: string;
  media?: HTMLImageElement | React.ReactNode;
  subHeader?: boolean;
}

interface EmtChipSelectProps {
  /**
   * The list of options to display in the dropdown
   */
  options: SelectOptionType[];
  /**
   * Current selected value
   */
  value?: string | number | (string | number)[];
  /**
   * Callback fired when an option is selected
   */
  onChange?: (value: string | number | (string | number)[]) => void;
  /**
   * Placeholder text if no value is selected
   */
  placeholder?: string;
  /**
   * Disables the chip and the select dropdown
   * @default false
   */
  disabled?: boolean;
  /**
   * If true, the select will allow multiple selections
   * @default false
   */
  multiple?: boolean;
  /**
   * Optional prop to customize the arrow icon className
   */
  arrowIconClassName?: string;
  /**
   * The sx prop to style the root component
   */
  sx?: SxProps<Theme>;
}

export const EmtChipSelect: React.FC<EmtChipSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select",
  disabled = false,
  multiple = false,
  arrowIconClassName = "emt-down-1",
  sx,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentValue, setCurrentValue] = useState(value);
  const [maxWidth, setMaxWidth] = useState<number>(0);

  const hiddenRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  // Measure the widest menu item after the component mounts
  useEffect(() => {
    if (hiddenRef.current) {
      const items = hiddenRef.current.querySelectorAll(".measure-item");
      let widest = 0;
      items.forEach((item) => {
        const width = (item as HTMLElement).offsetWidth;
        if (width > widest) {
          widest = width;
        }
      });
      setMaxWidth(widest);
    }
  }, [options]);

  const handleChipClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleItemClick = (optionValue: string | number) => {
    if (multiple && Array.isArray(currentValue)) {
      // If multiple, add or remove from the array
      const selectedValues = currentValue.includes(optionValue)
        ? currentValue.filter((val) => val !== optionValue)
        : [...currentValue, optionValue];
      setCurrentValue(selectedValues);
      onChange && onChange(selectedValues);
    } else if (multiple && !Array.isArray(currentValue)) {
      // If it's multiple but current value is not an array yet, initialize it
      const newValues = [optionValue];
      setCurrentValue(newValues);
      onChange && onChange(newValues);
    } else {
      // Single select
      setCurrentValue(optionValue);
      onChange && onChange(optionValue);
      handleClose();
    }
  };

  const getLabelForValue = (val: unknown): string => {
    if (multiple && Array.isArray(val)) {
      const selectedOptions = options.filter((o) => val.includes(o.value));
      return selectedOptions.map((o) => o.label).join(", ");
    } else {
      const selectedOption = options.find((o) => o.value === val);
      return selectedOption ? selectedOption.label : placeholder;
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      {/* Hidden container for measurement */}
      <Box
        ref={hiddenRef}
        sx={{
          position: "absolute",
          top: "-9999px",
          left: "-9999px",
          visibility: "hidden",
        }}
      >
        {options.map((option, index) => (
          <Box
            key={index}
            className="measure-item"
            sx={{
              display: "inline-block",
              border: "1px solid #AE59C6",
              borderRadius: "12px",
              padding: "0 12px",
              fontFamily: "inherit",
              fontSize: "14px",
              lineHeight: "32px",
            }}
          >
            {option.label}
          </Box>
        ))}
      </Box>

      <Box
        position="relative"
        sx={sx}
        width={maxWidth > 0 ? `${maxWidth}px` : "auto"}
      >
        <EmtChip
          sx={{
            border: "1px solid #AE59C6",
            width: "100%",
            height: "100%",
            borderRadius: "12px",
            "& .MuiChip-label": { width: "100%" },
          }}
          color="purplelight"
          label={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: "4px",
                "& .emt-icons": { fontSize: "10px!important", fontWeight: 700 },
              }}
            >
              <span>{getLabelForValue(currentValue)}</span>
              <EmtIcon className={arrowIconClassName} />
            </Box>
          }
          onClick={handleChipClick}
          disabled={disabled}
        />

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          PaperProps={{
            style: {
              width: maxWidth > 0 ? `${maxWidth}px` : undefined,
            },
          }}
          sx={{
            maxHeight: 300,
            "& .MuiMenu-paper": {
              border: "1px solid #AE59C6",
            },
            "& ul": {
              backgroundColor: "#fff!important",
            },
          }}
        >
          <Box
            sx={{
              maxHeight: 300,
              width: "auto",
              padding: "6px",
            }}
          >
            {options.map((option, index) => {
              const isSelected =
                multiple && Array.isArray(currentValue)
                  ? currentValue.includes(option.value)
                  : currentValue === option.value;

              return (
                <MenuItem
                  key={index}
                  onClick={() => handleItemClick(option.value)}
                  selected={isSelected}
                  sx={{
                    padding: "0",
                    backgroundColor: "transparent!important",
                    minHeight: "unset!important",
                  }}
                >
                  <EmtChip
                    label={option.label}
                    color={isSelected ? "purplelight" : "white"}
                    sx={{
                      width: "100%",
                      height: "32px",
                      borderRadius: "12px",
                      justifyContent: "flex-start",
                      "&:hover": { backgroundColor: "#f0f0f0!important" },
                    }}
                  />
                </MenuItem>
              );
            })}
          </Box>
        </Menu>
      </Box>
    </>
  );
};
